import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Scale, AlertTriangle } from 'lucide-react';

const LegalNoticePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-950" data-testid="legal-notice-page">
      {/* Header */}
      <div className="bg-gray-900/50 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-b from-gray-900/50 to-transparent py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Scale className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">Legal Notice</h1>
              <p className="text-gray-400 text-sm mt-1">Last Updated: February 2025</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 pb-20">
        <div className="prose prose-invert prose-amber max-w-none">
          
          {/* Introduction */}
          <div className="bg-gray-900/30 rounded-2xl p-6 mb-8 border border-white/5">
            <p className="text-gray-300 leading-relaxed m-0">
              The following terms and conditions of this Legal Notice apply to all visitors to the website of 
              Gegadyne Energy Private Limited (hereinafter referred to as "Gegadyne Energy", "Company", "We", "Us" or "Our"). 
              We reserve the right to amend this Legal Notice from time to time without prior notice.
            </p>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-8 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-amber-200 text-sm m-0">
              Please read these terms carefully before using this website, especially terms regarding disclaimer 
              and governing law. Your access, browsing, and/or use of this website signifies that you have accepted 
              all the following terms and conditions. If you do not agree with these terms, please immediately 
              stop visiting this website.
            </p>
          </div>

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mt-8 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-bold">1</span>
              Website Information
            </h2>
            <p className="text-gray-400">
              All information and data published on this website, including but not limited to text, images, graphics, 
              data, videos, product specifications, and technical documentation, are provided for general informational 
              purposes only. While we endeavour to ensure that the information is timely, accurate, and complete, 
              Gegadyne Energy makes no warranties or representations of any kind, express or implied, regarding the 
              accuracy, completeness, suitability, reliability, or availability of any information on this website.
            </p>
            <p className="text-gray-400">
              The Company reserves the right of final interpretation for all information and data published on this website. 
              Any reliance you place on such information is strictly at your own risk.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mt-8 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-bold">2</span>
              Modifications to Website
            </h2>
            <p className="text-gray-400">
              Gegadyne Energy may modify, update, or remove any content on this website at any time without prior notice. 
              We recommend that you visit this website regularly to stay informed of the latest information. The Company 
              shall not be liable for any consequences arising from such modifications or updates.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mt-8 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-bold">3</span>
              Prohibited Use
            </h2>
            <p className="text-gray-400">
              You are prohibited from:
            </p>
            <ul className="text-gray-400 space-y-2">
              <li>Modifying, altering, or tampering with any information or content on this website</li>
              <li>Copying, reproducing, republishing, or redistributing any content for public or commercial purposes without prior written consent</li>
              <li>Using any automated systems, bots, or scripts to access, scrape, or collect data from this website</li>
              <li>Using the content of this website for any unlawful or unauthorized purposes</li>
              <li>Attempting to gain unauthorized access to any part of this website or its systems</li>
            </ul>
            <p className="text-gray-400">
              Gegadyne Energy reserves the right to take appropriate legal action against any person found to be in 
              violation of these terms, including civil and criminal proceedings under applicable Indian laws.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mt-8 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-bold">4</span>
              User Communications
            </h2>
            <p className="text-gray-400">
              Gegadyne Energy provides contact forms and enquiry sections for visitors to communicate with us. 
              We may review messages submitted through these channels periodically. However, we shall not be liable 
              for the content of such messages submitted by visitors.
            </p>
            <p className="text-gray-400">
              While we generally treat communications as confidential, any message containing content that is 
              defamatory, obscene, threatening, infringing, or otherwise objectionable may be deleted at our sole 
              discretion. The user posting such content shall bear all liabilities arising therefrom, including 
              civil and criminal liability under Indian law.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mt-8 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-bold">5</span>
              Third-Party Links
            </h2>
            <p className="text-gray-400">
              For your convenience, this website may contain links to third-party websites. These links are provided 
              solely for informational purposes and do not constitute an endorsement, approval, or recommendation of 
              the content, products, or services offered by such third parties.
            </p>
            <p className="text-gray-400">
              Gegadyne Energy makes no warranties regarding the content, accuracy, or availability of third-party 
              websites and shall not be liable for any loss or damage arising from your use of such websites. 
              You should exercise independent judgment when accessing third-party websites.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mt-8 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-bold">6</span>
              Intellectual Property Rights
            </h2>
            <p className="text-gray-400">
              All trademarks, logos, service marks, trade names, and other intellectual property displayed on this 
              website are the property of Gegadyne Energy Private Limited or their respective owners. This includes, 
              but is not limited to, the "Gegadyne" name, logo, and "Multi-Ion" technology branding.
            </p>
            <p className="text-gray-400">
              No person may use, reproduce, or display any trademark, logo, or intellectual property of Gegadyne Energy 
              without prior written consent. Unauthorized use may result in legal action under the Trade Marks Act, 1999, 
              the Copyright Act, 1957, and other applicable Indian laws.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mt-8 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-bold">7</span>
              Copyright
            </h2>
            <p className="text-gray-400">
              All content on this website, including but not limited to text, graphics, images, photographs, videos, 
              audio, software, and documentation, is protected by copyright under the Copyright Act, 1957 of India 
              and international copyright treaties.
            </p>
            <p className="text-gray-400">
              The copyright in all such content is owned by Gegadyne Energy Private Limited or its licensors. 
              No part of this website may be reproduced, distributed, transmitted, or stored without prior written 
              permission from the Company, except as permitted under applicable law.
            </p>
          </section>

          {/* Section 8 - Disclaimer */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mt-8 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-bold">8</span>
              Disclaimer of Liability
            </h2>
            <div className="bg-gray-900/50 rounded-xl p-6 border border-white/10">
              <p className="text-gray-400 mb-4">
                To the fullest extent permitted by applicable law, Gegadyne Energy shall not be liable for:
              </p>
              <ul className="text-gray-400 space-y-2 mb-4">
                <li>Any direct, indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, revenue, data, business opportunities, or goodwill</li>
                <li>Business interruption or any other commercial damages or losses</li>
                <li>Any errors, omissions, or inaccuracies in the content of this website</li>
                <li>Any unauthorized access to or use of our servers and/or personal information</li>
                <li>Any bugs, viruses, malware, or other harmful code transmitted through this website</li>
                <li>Any technical failures, system crashes, or interruptions in website availability</li>
              </ul>
              <p className="text-gray-400 m-0">
                This disclaimer applies regardless of the theory of liability, whether based on contract, tort, 
                negligence, strict liability, or any other legal theory.
              </p>
            </div>
          </section>

          {/* Section 9 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mt-8 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-bold">9</span>
              Privacy
            </h2>
            <p className="text-gray-400">
              Gegadyne Energy respects and protects visitors' personal information and privacy rights in accordance 
              with applicable Indian laws, including the Information Technology Act, 2000 and the Digital Personal 
              Data Protection Act, 2023. Our collection, processing, storage, and sharing of personal information 
              is governed by our <Link to="/privacy" className="text-emerald-400 hover:text-emerald-300">Privacy Policy</Link>.
            </p>
            <p className="text-gray-400">
              By using this website, you consent to the terms of our Privacy Policy.
            </p>
          </section>

          {/* Section 10 - Governing Law */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mt-8 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-bold">10</span>
              Governing Law and Dispute Resolution
            </h2>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-6">
              <p className="text-gray-300 mb-4">
                This Legal Notice and your use of this website shall be governed by and construed in accordance with 
                the laws of India, without regard to its conflict of law principles.
              </p>
              <p className="text-gray-300 mb-4">
                Any dispute, controversy, or claim arising out of or in connection with this website, this Legal Notice, 
                or your use of any content or services provided through this website shall be resolved as follows:
              </p>
              <ol className="text-gray-300 space-y-2 list-decimal pl-5 mb-4">
                <li>
                  <strong className="text-white">Amicable Settlement:</strong> The parties shall first attempt to 
                  resolve the dispute through good faith negotiations.
                </li>
                <li>
                  <strong className="text-white">Mediation:</strong> If negotiations fail within 30 days, the parties 
                  may attempt to resolve the dispute through mediation in Mumbai.
                </li>
                <li>
                  <strong className="text-white">Exclusive Jurisdiction:</strong> If the dispute cannot be resolved 
                  through negotiation or mediation, it shall be submitted to the exclusive jurisdiction of the 
                  competent courts located in <strong className="text-amber-400">Mumbai, Maharashtra, India</strong>.
                </li>
              </ol>
              <p className="text-gray-300 m-0">
                You hereby consent to the personal jurisdiction and venue of such courts and waive any objection 
                based on inconvenient forum.
              </p>
            </div>
          </section>

          {/* Section 11 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mt-8 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-bold">11</span>
              Indemnification
            </h2>
            <p className="text-gray-400">
              You agree to indemnify, defend, and hold harmless Gegadyne Energy Private Limited, its directors, 
              officers, employees, agents, and affiliates from and against any and all claims, damages, losses, 
              liabilities, costs, and expenses (including reasonable legal fees) arising out of or in connection with:
            </p>
            <ul className="text-gray-400 space-y-2">
              <li>Your use of this website</li>
              <li>Your violation of this Legal Notice</li>
              <li>Your violation of any applicable law or regulation</li>
              <li>Your infringement of any intellectual property or other rights of any third party</li>
            </ul>
          </section>

          {/* Section 12 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mt-8 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-bold">12</span>
              Severability
            </h2>
            <p className="text-gray-400">
              If any provision of this Legal Notice is found to be invalid, illegal, or unenforceable by a court 
              of competent jurisdiction, such invalidity, illegality, or unenforceability shall not affect the 
              remaining provisions, which shall continue in full force and effect.
            </p>
          </section>

          {/* Section 13 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mt-8 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-bold">13</span>
              Contact Information
            </h2>
            <p className="text-gray-400">
              For any questions or concerns regarding this Legal Notice, please contact us at:
            </p>
            <div className="bg-gray-900/50 rounded-xl p-6 my-4 border border-white/10">
              <p className="text-gray-300 m-0">
                <strong className="text-white">Gegadyne Energy Private Limited</strong><br />
                Mumbai, Maharashtra, India<br /><br />
                Email: <a href="mailto:info@gegadyne.com" className="text-emerald-400 hover:text-emerald-300">info@gegadyne.com</a>
              </p>
            </div>
          </section>

          {/* Footer Note */}
          <div className="bg-gray-900/30 rounded-xl p-6 mt-12 border border-white/5">
            <p className="text-gray-500 text-sm m-0">
              By continuing to access and use this website, you acknowledge that you have read, understood, and 
              agree to be bound by this Legal Notice and all applicable laws and regulations of India.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LegalNoticePage;
